import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Language as WebsiteIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Article as ArticleIcon,
  Replay as RetryIcon,
} from "@mui/icons-material";

const ADD_WEBSITE_SOURCE = gql`
  mutation AddWebsiteSource($widgetId: ID!, $input: AddWebsiteSourceInput!) {
    addWebsiteSource(widgetId: $widgetId, input: $input) {
      id
      sources {
        websiteUrls {
          url
          crawlDepth
          crawlStatus
          pagesCrawled
          errorMessage
        }
      }
    }
  }
`;

const ADD_DOCUMENTATION_URL = gql`
  mutation AddDocumentationUrl(
    $widgetId: ID!
    $input: AddDocumentationUrlInput!
  ) {
    addDocumentationUrl(widgetId: $widgetId, input: $input) {
      id
      sources {
        documentationUrls {
          url
          title
          fetchStatus
        }
      }
    }
  }
`;

const UPLOAD_BOT_DOCUMENT = gql`
  mutation UploadBotDocument($widgetId: ID!, $file: Upload!) {
    uploadBotDocument(widgetId: $widgetId, file: $file) {
      fileId
      originalName
      processStatus
    }
  }
`;

const DELETE_KNOWLEDGE_SOURCE = gql`
  mutation DeleteKnowledgeSource(
    $widgetId: ID!
    $sourceType: String!
    $sourceId: String!
  ) {
    removeKnowledgeSource(
      widgetId: $widgetId
      sourceType: $sourceType
      sourceId: $sourceId
    ) {
      id
      widgetId
    }
  }
`;

const TRIGGER_REINDEX = gql`
  mutation TriggerReindex($widgetId: ID!) {
    triggerReindex(widgetId: $widgetId) {
      status
      lastIndexedAt
      totalChunks
      totalDocuments
      indexingProgress
    }
  }
`;

const RECRAWL_WEBSITE_SOURCE = gql`
  mutation RecrawlWebsiteSource($widgetId: ID!, $url: String!) {
    recrawlWebsiteSource(widgetId: $widgetId, url: $url) {
      id
      sources {
        websiteUrls {
          url
          crawlDepth
          crawlStatus
          pagesCrawled
          errorMessage
        }
      }
    }
  }
`;

const getStatusChip = (status) => {
  const statusConfig = {
    completed: { color: "success", icon: <SuccessIcon fontSize="small" /> },
    processing: { color: "warning", icon: <PendingIcon fontSize="small" /> },
    pending: { color: "default", icon: <PendingIcon fontSize="small" /> },
    crawling: { color: "info", icon: <CircularProgress size={14} /> },
    failed: { color: "error", icon: <ErrorIcon fontSize="small" /> },
  };
  const config = statusConfig[status] || statusConfig.pending;
  return (
    <Chip label={status} size="small" color={config.color} icon={config.icon} />
  );
};

function KnowledgeBasePanel({
  widgetId,
  knowledgeBase,
  loading,
  onRefetch,
  showAlert,
}) {
  const [addWebsiteDialog, setAddWebsiteDialog] = useState(false);
  const [addDocDialog, setAddDocDialog] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [docUrl, setDocUrl] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const [addWebsiteSource, { loading: addingWebsite }] = useMutation(
    ADD_WEBSITE_SOURCE,
    {
      onCompleted: () => {
        showAlert("Website source added successfully", "success");
        setAddWebsiteDialog(false);
        setWebsiteUrl("");
        setCrawlDepth(2);
        onRefetch?.();
      },
      onError: (error) => showAlert(`Error: ${error.message}`, "error"),
    }
  );

  const [addDocumentationUrl, { loading: addingDoc }] = useMutation(
    ADD_DOCUMENTATION_URL,
    {
      onCompleted: () => {
        showAlert("Documentation URL added successfully", "success");
        setAddDocDialog(false);
        setDocUrl("");
        setDocTitle("");
        onRefetch?.();
      },
      onError: (error) => showAlert(`Error: ${error.message}`, "error"),
    }
  );

  const [uploadDocument] = useMutation(UPLOAD_BOT_DOCUMENT, {
    onCompleted: () => {
      showAlert("Document uploaded successfully", "success");
      setUploading(false);
      onRefetch?.();
    },
    onError: (error) => {
      showAlert(`Error: ${error.message}`, "error");
      setUploading(false);
    },
  });

  const [deleteSource, { loading: deleting }] = useMutation(
    DELETE_KNOWLEDGE_SOURCE,
    {
      onCompleted: () => {
        showAlert("Source deleted successfully", "success");
        onRefetch?.();
      },
      onError: (error) => showAlert(`Error: ${error.message}`, "error"),
    }
  );

  const [triggerReindex, { loading: reindexing }] = useMutation(
    TRIGGER_REINDEX,
    {
      onCompleted: () => {
        showAlert("Reindexing started", "success");
        onRefetch?.();
      },
      onError: (error) => showAlert(`Error: ${error.message}`, "error"),
    }
  );

  const [recrawlWebsite, { loading: recrawling }] = useMutation(
    RECRAWL_WEBSITE_SOURCE,
    {
      onCompleted: () => {
        showAlert("Recrawling started", "success");
        onRefetch?.();
      },
      onError: (error) => showAlert(`Error: ${error.message}`, "error"),
    }
  );

  const handleRecrawl = (url) => {
    recrawlWebsite({
      variables: { widgetId, url },
    });
  };

  const handleAddWebsite = () => {
    addWebsiteSource({
      variables: {
        widgetId,
        input: { url: websiteUrl, crawlDepth },
      },
    });
  };

  const handleAddDocumentation = () => {
    addDocumentationUrl({
      variables: {
        widgetId,
        input: { url: docUrl, title: docTitle },
      },
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    if (!allowedTypes.includes(file.type)) {
      showAlert("Please upload a PDF, DOCX, or TXT file", "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showAlert("File size must be less than 10MB", "error");
      return;
    }

    setUploading(true);
    uploadDocument({
      variables: { widgetId, file },
    });
  };

  const handleDeleteSource = (sourceType, sourceId) => {
    deleteSource({
      variables: { widgetId, sourceType, sourceId },
    });
  };

  const handleReindex = () => {
    triggerReindex({ variables: { widgetId } });
  };

  const sources = knowledgeBase?.sources || {};
  const indexingStatus = knowledgeBase?.indexingStatus;
  const stats = knowledgeBase?.stats;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Knowledge Base
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add content sources for your AI Bot to learn from
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={
            reindexing ? <CircularProgress size={20} /> : <RefreshIcon />
          }
          onClick={handleReindex}
          disabled={reindexing}
        >
          {reindexing ? "Reindexing..." : "Re-index All"}
        </Button>
      </Box>

      {/* Indexing Status */}
      {indexingStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                {getStatusChip(indexingStatus.status)}
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Total Documents
                </Typography>
                <Typography variant="h6">
                  {indexingStatus.totalDocuments || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Total Chunks
                </Typography>
                <Typography variant="h6">
                  {indexingStatus.totalChunks || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Queries Answered
                </Typography>
                <Typography variant="h6">
                  {stats?.totalQueriesAnswered || 0}
                </Typography>
              </Grid>
              {indexingStatus.status === "processing" &&
                indexingStatus.indexingProgress && (
                  <Grid item xs={12}>
                    <LinearProgress
                      variant="determinate"
                      value={indexingStatus.indexingProgress}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Progress: {indexingStatus.indexingProgress}%
                    </Typography>
                  </Grid>
                )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Website URLs Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  <WebsiteIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Website URLs
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddWebsiteDialog(true)}
                >
                  Add Website
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {sources.websiteUrls?.length > 0 ? (
                <List dense>
                  {sources.websiteUrls.map((site, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <WebsiteIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={site.url}
                        secondary={`Depth: ${site.crawlDepth} | Pages: ${
                          site.pagesCrawled || 0
                        }${
                          site.errorMessage
                            ? ` | Error: ${site.errorMessage}`
                            : ""
                        }`}
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" alignItems="center" gap={1}>
                          {site.crawlStatus === "failed" ? (
                            <Tooltip
                              title={
                                site.errorMessage ||
                                "Crawl failed. Click to retry."
                              }
                            >
                              {getStatusChip(site.crawlStatus)}
                            </Tooltip>
                          ) : (
                            getStatusChip(site.crawlStatus)
                          )}
                          {site.crawlStatus === "failed" && (
                            <Tooltip title="Retry crawl">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleRecrawl(site.url)}
                                disabled={recrawling}
                              >
                                {recrawling ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <RetryIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleDeleteSource("website", site.url)
                            }
                            disabled={deleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  textAlign="center"
                  py={2}
                >
                  No website URLs added yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Documentation URLs Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  <ArticleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Documentation URLs
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDocDialog(true)}
                >
                  Add Documentation
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {sources.documentationUrls?.length > 0 ? (
                <List dense>
                  {sources.documentationUrls.map((doc, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ArticleIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.title || doc.url}
                        secondary={doc.url}
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusChip(doc.fetchStatus)}
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleDeleteSource("documentation", doc.url)
                            }
                            disabled={deleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  textAlign="center"
                  py={2}
                >
                  No documentation URLs added yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Uploaded Files Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  <DocumentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Uploaded Documents
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={
                    uploading ? <CircularProgress size={20} /> : <UploadIcon />
                  }
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Document"}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                  />
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary" paragraph>
                Supported formats: PDF, DOCX, TXT (max 10MB)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {sources.uploadedFiles?.length > 0 ? (
                <List dense>
                  {sources.uploadedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <DocumentIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.originalName}
                        secondary={`Size: ${(file.fileSize / 1024).toFixed(
                          1
                        )} KB | Chunks: ${file.chunksGenerated || 0}`}
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusChip(file.processStatus)}
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleDeleteSource("file", file.fileId)
                            }
                            disabled={deleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  textAlign="center"
                  py={2}
                >
                  No documents uploaded yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Website Dialog */}
      <Dialog
        open={addWebsiteDialog}
        onClose={() => setAddWebsiteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Website URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Website URL"
            type="url"
            fullWidth
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Crawl Depth</InputLabel>
            <Select
              value={crawlDepth}
              label="Crawl Depth"
              onChange={(e) => setCrawlDepth(e.target.value)}
            >
              <MenuItem value={1}>1 - Homepage only</MenuItem>
              <MenuItem value={2}>2 - Homepage + 1 level deep</MenuItem>
              <MenuItem value={3}>3 - Homepage + 2 levels deep</MenuItem>
              <MenuItem value={5}>5 - Deep crawl (up to 4 levels)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWebsiteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddWebsite}
            variant="contained"
            disabled={!websiteUrl || addingWebsite}
          >
            {addingWebsite ? "Adding..." : "Add Website"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Documentation Dialog */}
      <Dialog
        open={addDocDialog}
        onClose={() => setAddDocDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Documentation URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={docTitle}
            onChange={(e) => setDocTitle(e.target.value)}
            placeholder="Product Documentation"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Documentation URL"
            type="url"
            fullWidth
            value={docUrl}
            onChange={(e) => setDocUrl(e.target.value)}
            placeholder="https://docs.example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDocDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddDocumentation}
            variant="contained"
            disabled={!docUrl || addingDoc}
          >
            {addingDoc ? "Adding..." : "Add Documentation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default KnowledgeBasePanel;
